import React from 'react';
import styled from 'styled-components';

const OptionsContainer = styled.div`
  margin: 20px 0;
  padding: 15px;
  background-color: #f5f5f5;
  border-radius: 4px;

  @media (max-width: 767px) {
    margin: 15px 0;
    padding: 12px;
  }
`;

const OptionSetWrapper = styled.div`
  margin-bottom: 15px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background-color: white;

  &:focus {
    outline: none;
    border-color: #4caf50;
  }

  @media (max-width: 767px) {
    padding: 10px 12px;
    font-size: 16px; /* Prevents zoom on iOS */
  }
`;

const SelectedOption = styled.div`
  margin-top: 8px;
  padding: 8px 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  font-size: 14px;
  color: #666;
`;

const SelectedLabel = styled.span`
  font-weight: 500;
  color: #333;
  margin-right: 4px;
`;

const ProductOptionsSelector = ({
  product,
  selectedOptions,
  onOptionChange,
}) => {
  // Use product_option_sets if available, otherwise fall back to legacy product_options
  const optionSets = product.product_option_sets || [];
  const hasNewStructure = optionSets.length > 0;

  // Legacy support: convert product_options to a single option set
  const legacyOptionSet =
    !hasNewStructure && product.product_options?.length > 0
      ? {
          id: 'legacy',
          key: 'legacy',
          name: 'Product Options',
          display_name: 'Product Option',
          product_options: product.product_options,
        }
      : null;

  const allOptionSets = hasNewStructure
    ? optionSets
    : legacyOptionSet
    ? [legacyOptionSet]
    : [];

  if (allOptionSets.length === 0) {
    return null;
  }

  return (
    <OptionsContainer>
      {allOptionSets.map((optionSet) => {
        const selectedOption = selectedOptions?.[optionSet.key];
        const selectedOptionData = optionSet.product_options?.find(
          (opt) => opt.key === selectedOption || opt.id === selectedOption,
        );

        return (
          <OptionSetWrapper key={optionSet.id}>
            <Label htmlFor={`option-set-${optionSet.id}`}>
              {optionSet.display_name || optionSet.name || 'Product Option'}
            </Label>
            <Select
              id={`option-set-${optionSet.id}`}
              name={`option-set-${optionSet.id}`}
              value={selectedOption || ''}
              onChange={(e) => onOptionChange(optionSet.key, e.target.value)}
            >
              <option value="">Select...</option>
              {optionSet.product_options?.map((option) => {
                const priceDisplay = option.price
                  ? ` (+$${(option.price / 100).toFixed(2)})`
                  : '';
                return (
                  <option key={option.id} value={option.key || option.id}>
                    {option.name}
                    {priceDisplay}
                  </option>
                );
              })}
            </Select>

            {selectedOptionData && (
              <SelectedOption>
                <SelectedLabel>Selected:</SelectedLabel>
                {selectedOptionData.name}
                {selectedOptionData.price > 0 && (
                  <span style={{ marginLeft: '8px' }}>
                    (+${(selectedOptionData.price / 100).toFixed(2)})
                  </span>
                )}
              </SelectedOption>
            )}
          </OptionSetWrapper>
        );
      })}
    </OptionsContainer>
  );
};

export default ProductOptionsSelector;
